import React from "react"
import { useStore } from "@/store"
import { getHoursWrapped } from "@/components/calendar_view/_utils"

type ClosedLayerProps = {
  closed: boolean
  startHour: number
  endHour: number
  openingStartHour: number
  openingEndHour: number
}

/**
 * Calculate the ranges of hours that are closed (outside opening hours).
 * Returns an array of { startIndex, endIndex } representing ranges in the hours array
 * that should display the closed overlay.
 */
const getClosedHourRanges = (
  hours: number[],
  openingStartHour: number,
  openingEndHour: number,
): Array<{ startIndex: number; endIndex: number }> => {
  if (hours.length === 0) return []

  const ranges: Array<{ startIndex: number; endIndex: number }> = []

  // Handle cross-midnight opening hours
  const openingEndWrapped = openingEndHour < openingStartHour ? openingEndHour + 24 : openingEndHour

  // Find indices in the hours array that are outside opening hours
  let currentRangeStart: number | null = null

  for (let i = 0; i < hours.length; i++) {
    const hour = hours[i]

    // Calculate the "unwrapped" hour for comparison
    // Hours after midnight (0-6) when we have cross-midnight display should be treated as 24+
    let unwrappedHour = hour
    if (i > 0 && hour < hours[0]) {
      // This hour is after midnight in a cross-midnight display
      unwrappedHour = hour + 24
    }

    // Check if this hour is outside opening hours
    // A hour slot represents the time from that hour to the next hour
    // If venue closes at X:00, then hour slot X is "closed" (represents X:00 to X+1:00)
    const isBeforeOpening = unwrappedHour < openingStartHour
    const isAfterClosing = unwrappedHour >= openingEndWrapped

    const isClosed = isBeforeOpening || isAfterClosing

    if (isClosed) {
      if (currentRangeStart === null) {
        currentRangeStart = i
      }
    } else {
      if (currentRangeStart !== null) {
        ranges.push({ startIndex: currentRangeStart, endIndex: i })
        currentRangeStart = null
      }
    }
  }

  // Close any remaining range
  if (currentRangeStart !== null) {
    ranges.push({ startIndex: currentRangeStart, endIndex: hours.length })
  }

  return ranges
}

const ClosedOverlay = ({ top, height, width }: { top: number; height: number; width: number }) => (
  <div
    className="absolute left-0 touch-none pointer-events-none z-1 bg-[repeating-linear-gradient(45deg,#fafafa_0px,#fafafa_8px,transparent_8px,transparent_16px)]"
    style={{
      position: "absolute",
      top,
      left: 0,
      width,
      height,
    }}
    data-testid="panel-reservations-calendar-closed-overlay"
  />
)

const ClosedLayer = ({ closed, startHour, endHour, openingStartHour, openingEndHour }: ClosedLayerProps) => {
  const { calendarDimensions } = useStore()

  // If the venue is fully closed, show full overlay
  if (closed) {
    return (
      <div
        className="absolute left-0 touch-none pointer-events-none items-center z-1 bg-[repeating-linear-gradient(45deg,#fafafa_0px,#fafafa_8px,transparent_8px,transparent_16px)]"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: calendarDimensions.canvas.width,
          height: calendarDimensions.canvas.height,
        }}
        data-testid="panel-reservations-calendar-closed-overlay-full"
      />
    )
  }

  // Get all visible hours
  const hours = getHoursWrapped(startHour, endHour)

  // Calculate which hour ranges are closed
  const closedRanges = getClosedHourRanges(hours, openingStartHour, openingEndHour)

  // If no closed ranges, nothing to render
  if (closedRanges.length === 0) {
    return null
  }

  const { timeline, canvas } = calendarDimensions

  return (
    <>
      {closedRanges.map((range, index) => {
        // Calculate top position and height based on hour indices
        const top = range.startIndex * timeline.offset
        const height = (range.endIndex - range.startIndex) * timeline.offset

        return <ClosedOverlay key={`closed-range-${index}`} top={top} height={height} width={canvas.width} />
      })}
    </>
  )
}

export default ClosedLayer
